import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraDivider, SpecraOverlayDrawer, SpecraOverlayFooter, SpecraSectionHeader } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Pencil } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KpiInfo } from '../../../../components/delivery/widgets/KpiTile';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { FieldControl } from './EntityForm';
/**
 * A right-side drawer for the full breakdown of a record (deviation / risk /
 * issue / score / touchpoint), on the SpecraOverlayDrawer shell: standard
 * header (title + subtitle + close), hairline-separated sections with
 * uppercase group labels, and label-above-value rows. Rows flagged `masked`
 * honor the same field masking the table applies.
 *
 * With an `edit` config it also edits IN PLACE: the read layout stays and each
 * row's value cell becomes its control (`editField` / `editControl`); a pinned
 * Save/Cancel footer commits. Consumers that pass no `edit` stay read-only.
 */ export function ScoreDetailDrawer({ open, onOpenChange, title, description, sections, testid, resource, maskAs, children, edit, canEdit, initialMode = 'read', extraHeaderAction }) {
    const [isEditing, setIsEditing] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const canEditNow = !!edit && !!canEdit;
    const rowIdBase = useId();
    // Consumers derive title/sections from the selected record and null it on
    // close — snapshot the last open content so the slide-out animation keeps
    // showing it instead of an emptied shell.
    const lastContent = useRef({
        title,
        description,
        sections,
        children
    });
    if (open) lastContent.current = {
        title,
        description,
        sections,
        children
    };
    const shown = open ? {
        title,
        description,
        sections,
        children
    } : lastContent.current;
    // Type-erased at the form boundary (same contract as EntityForm); the caller's
    // concrete TEdit shape is cast back at the defaultValues / onSubmit edges.
    const { control, handleSubmit, reset, formState, watch, getValues, setValue } = useForm({
        defaultValues: edit?.defaultValues ?? {},
        mode: 'onTouched'
    });
    // Enter edit mode: seed out-of-form state (pickers), reset the form from the
    // record, and clear any stale submit error.
    const startEdit = ()=>{
        if (!canEditNow) return;
        edit?.onEditStart?.();
        reset(edit?.defaultValues ?? {});
        setSubmitError(null);
        setIsEditing(true);
    };
    // On open: honor initialMode (a row's Edit action opens straight into edit).
    // On close: always drop back to read so the next open starts clean. Keyed on
    // `open` only — this must run on the open edge, not re-fire when initialMode /
    // canEditNow / startEdit identities change mid-session.
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional open-edge effect
    useEffect(()=>{
        if (!open) {
            setIsEditing(false);
            setSubmitError(null);
        } else if (initialMode === 'edit' && canEditNow) {
            startEdit();
        }
    }, [
        open
    ]);
    const renderValue = (row)=>{
        if (!row.masked) return row.value;
        const fieldName = row.maskFieldName ?? maskAs;
        if (resource && fieldName) {
            return /*#__PURE__*/ _jsx(MaskedField, {
                fieldName: fieldName,
                resource: resource,
                children: row.value
            });
        }
        // A row flagged `masked` without a resource + token is a caller
        // misconfiguration. This is a redaction primitive, so fail closed: render
        // the placeholder rather than leak the raw value it was told to protect.
        return /*#__PURE__*/ _jsx("span", {
            className: "select-none text-sm italic text-muted-foreground",
            "data-testid": Testids.maskedField.placeholder,
            "data-view-mode": "misconfigured",
            children: "Restricted"
        });
    };
    const editing = isEditing && !!edit;
    // Passed to a function-form `children` (see ScoreDetailFormAccess) — built
    // once per render so identity churn doesn't matter, same as EntityForm's
    // extraContent access object.
    const formAccess = {
        editing,
        watch: (name)=>watch(name),
        getValues: (name)=>getValues(name),
        setValue: (name, value)=>setValue(name, value),
        startEdit
    };
    // Resolve BEFORE checking truthiness: a function child must be judged on
    // what it renders, not on the function reference itself (always truthy) —
    // otherwise a function that legitimately renders nothing (e.g. gated on
    // `detail` being null) would still wrap an empty hairline-bordered div.
    const renderedChildren = typeof shown.children === 'function' ? shown.children(formAccess) : shown.children;
    const sectionsBody = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            shown.sections.map((section, i)=>/*#__PURE__*/ _jsxs("div", {
                    className: i === 0 ? '' : 'mt-[var(--space-5-5)]',
                    children: [
                        section.heading ? /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            title: section.heading
                        }) : i > 0 ? /*#__PURE__*/ _jsx(SpecraDivider, {}) : null,
                        /*#__PURE__*/ _jsx("dl", {
                            className: section.layout === 'columns' ? 'mt-2 grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-x-4 gap-y-3' : 'mt-2 space-y-3',
                            children: section.rows.map((row, j)=>{
                                // A self-labeled custom control (a picker) replaces the whole row
                                // in edit mode — no <dt>, so its own label isn't duplicated.
                                if (editing && row.editControl) {
                                    return /*#__PURE__*/ _jsx("div", {
                                        children: row.editControl
                                    }, row.label);
                                }
                                const dtId = `${rowIdBase}-${i}-${j}`;
                                return /*#__PURE__*/ _jsxs("div", {
                                    children: [
                                        /*#__PURE__*/ _jsxs("dt", {
                                            id: dtId,
                                            className: "flex items-center gap-1 text-[length:var(--fs-ui-eyebrow)] text-muted-foreground",
                                            children: [
                                                row.label,
                                                row.labelInfo && /*#__PURE__*/ _jsx(KpiInfo, {
                                                    label: row.label,
                                                    tip: row.labelInfo,
                                                    testid: Testids.deliveryShared.infoTip,
                                                    dataName: row.editField ? String(row.editField.name) : undefined
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("dd", {
                                            className: editing && row.editField ? 'mt-1' : 'mt-[var(--space-0-5)] whitespace-pre-wrap break-words text-[length:var(--fs-ui)] text-foreground',
                                            children: editing && row.editField ? /*#__PURE__*/ _jsx(FieldControl, {
                                                field: row.editField,
                                                control: control,
                                                errors: formState.errors,
                                                labelledBy: dtId
                                            }) : renderValue(row)
                                        })
                                    ]
                                }, row.label);
                            })
                        })
                    ]
                }, section.heading ?? String(i))),
            renderedChildren && // Children (documents, history) bring their own group header and hairline.
            /*#__PURE__*/ _jsx("div", {
                className: "mt-[var(--space-5-5)]",
                children: renderedChildren
            })
        ]
    });
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: open,
        // Every dismissal (Escape, overlay click, the X) peels one layer: exit
        // edit mode first, close the drawer otherwise — abandoning half-typed
        // edits takes two deliberate steps, never one stray click.
        onOpenChange: (next)=>{
            if (!next && isEditing) {
                setIsEditing(false);
                return;
            }
            onOpenChange(next);
        },
        title: shown.title,
        description: shown.description,
        testId: testid,
        headerActions: !isEditing && (extraHeaderAction || canEditNow) ? /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-1",
            children: [
                extraHeaderAction,
                canEditNow && /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "ghost",
                    "data-testid": Testids.deliveryShared.drawerEdit,
                    onClick: startEdit,
                    "aria-label": "Edit",
                    iconLeft: /*#__PURE__*/ _jsx(Pencil, {
                        className: "h-[13px] w-[13px]"
                    }),
                    className: "text-muted-foreground hover:text-foreground",
                    children: "Edit"
                })
            ]
        }) : undefined,
        // The body is a bounded flex column; read and edit modes own their own
        // scroll region so the edit footer can pin (same pattern as EntityForm).
        scrollBody: false,
        bodyProps: {
            className: 'flex flex-col'
        },
        children: editing && edit ? /*#__PURE__*/ _jsxs("form", {
            className: "flex min-h-0 flex-1 flex-col",
            onSubmit: handleSubmit(async (data)=>{
                setSubmitError(null);
                try {
                    await edit.onSubmit(data);
                    setIsEditing(false);
                } catch (err) {
                    setSubmitError(err instanceof Error ? err.message : String(err));
                }
            }),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "min-h-0 flex-1 overflow-y-auto pr-1",
                    children: [
                        sectionsBody,
                        submitError && /*#__PURE__*/ _jsx("p", {
                            className: "mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive",
                            role: "alert",
                            "data-testid": Testids.deliveryShared.formError,
                            children: submitError
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                    className: "-mx-4 -mb-3 mt-4 sm:-mx-5 sm:-mb-4",
                    loading: edit.isSubmitting,
                    loadingLabel: "Saving…",
                    confirmLabel: edit.submitLabel ?? 'Save',
                    confirmDisabled: edit.isSubmitting,
                    onCancel: ()=>setIsEditing(false),
                    cancelProps: {
                        disabled: edit.isSubmitting,
                        'data-testid': Testids.deliveryShared.formCancel
                    },
                    confirmProps: {
                        type: 'submit',
                        'data-testid': Testids.deliveryShared.formSubmit
                    }
                })
            ]
        }) : /*#__PURE__*/ _jsx("div", {
            className: "min-h-0 flex-1 overflow-y-auto pr-1",
            children: sectionsBody
        })
    });
}
